// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useLayoutEffect, useState, type RefObject } from 'react';

export interface AnchoredPosition {
  top: number;
  left?: number;
  right?: number;
}

/**
 * Viewport coordinates for a dropdown anchored under `anchorRef`, meant to be
 * rendered through a portal (see `createPortal`) rather than as a normal
 * absolutely-positioned child.
 *
 * This matters specifically for header dropdowns: `.helios-header` has its
 * own `position: relative` + `z-index`, which makes it a stacking context.
 * Any descendant's z-index — no matter how high — is capped by that
 * context when compared to elements outside it, such as the Ask Helios
 * panel. Portaling to `document.body` and positioning by coordinates here
 * escapes that trap.
 */
export function useAnchoredPosition(
  anchorRef: RefObject<HTMLElement | null>,
  open: boolean,
  align: 'left' | 'right' = 'left'
): AnchoredPosition | null {
  const [position, setPosition] = useState<AnchoredPosition | null>(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setPosition(null);
      return;
    }
    const update = () => {
      if (!anchorRef.current) return;
      const rect = anchorRef.current.getBoundingClientRect();
      setPosition(
        align === 'right'
          ? { top: rect.bottom, right: window.innerWidth - rect.right }
          : { top: rect.bottom, left: rect.left }
      );
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open, anchorRef, align]);

  return position;
}
